/**
 * Market Types
 *
 * Define la estructura de datos para mercados (países de operación)
 * Cada mercado tiene su propia configuración financiera, regulatoria y catálogos
 */

// ========== ENUMS ==========

/**
 * Estado de configuración de un mercado
 */
export enum MarketStatus {
  ACTIVE = 'active',           // Mercado activo y completamente configurado
  INCOMPLETE = 'incomplete',   // Mercado agregado pero sin configuración completa
  INACTIVE = 'inactive',       // Mercado temporalmente desactivado
}

/**
 * Nivel de configuración requerida
 */
export enum ConfigLevel {
  REQUIRED = 'required',       // Configuración obligatoria
  OPTIONAL = 'optional',       // Configuración opcional
  NOT_APPLICABLE = 'not_applicable', // No aplica para este mercado
}

// ========== INTERFACES ==========

/**
 * Progreso de configuración de un mercado
 */
export interface MarketConfigProgress {
  basicInfo: boolean;          // Información básica (nombre, moneda principal)
  minimumWage: boolean;        // Salario mínimo configurado
  currencies: boolean;         // Al menos 1 moneda configurada
  subsidyPlans: boolean;       // Planes de subsidio configurados
  cities: boolean;             // Ciudades configuradas
}

/**
 * Salario mínimo de un mercado
 */
export interface MinimumWage {
  value: number;               // Valor numérico
  currency: string;            // ID de la moneda (ej: 'COP', 'USD')
  lastUpdated: Date;           // Fecha de última actualización
  effectiveDate?: Date;        // Fecha desde cuándo es efectivo
  notes?: string;              // Notas adicionales
}

/**
 * Plan de subsidio específico de un mercado
 */
export interface SubsidyPlan {
  id: string;
  code: string;                // Código corto (ej: 'VIS', 'VIP')
  name: string;                // Nombre completo
  description?: string;        // Descripción del plan
  maxValue?: number;           // Valor máximo del subsidio
  currency?: string;           // Moneda del subsidio
  isActive: boolean;           // Si está activo
  createdAt: Date;
  updatedAt: Date;
}


/**
 * Ciudad dentro de un mercado
 */
export interface MarketCity {
  id: string;
  name: string;
  state?: string;              // Estado/Provincia/Departamento
  postalCode?: string;
  isActive: boolean;
  activeProjects?: number;     // Número de proyectos activos en esta ciudad
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Regulaciones específicas de un mercado
 */
export interface MarketRegulations {
  requiresSubsidyPlan: ConfigLevel;        // ¿Requiere plan de subsidio?
  requiresMinimumWage: ConfigLevel;        // ¿Requiere salario mínimo?
  allowsForeignCurrency: boolean;          // ¿Permite ventas en moneda extranjera?
  customFields?: Record<string, any>;      // Campos personalizados por país
}

/**
 * Moneda asociada a un mercado
 */
export interface MarketCurrency {
  currencyId: string;          // ID de la moneda en el catálogo global
  isPrimary: boolean;          // ¿Es la moneda principal del mercado?
  isAccepted: boolean;         // ¿Se acepta para ventas?
  exchangeRate?: number;       // Tasa de cambio respecto a moneda principal
  lastUpdated?: Date;
}

/**
 * Configuración completa de un mercado (país de operación)
 */
export interface Market {
  // Identificación
  id: string;
  countryId: string;           // ID del país (catálogo global)
  isoCode: string;             // Código ISO del país (ej: 'CO', 'CR', 'DO')
  name: string;                // Nombre del país
  flag?: string;               // Emoji o URL de bandera

  // Estado
  status: MarketStatus;
  isActive: boolean;
  addedAt: Date;
  lastConfiguredAt?: Date;
  configuredBy?: string;       // ID del usuario que configuró

  // Progreso de configuración
  isConfigured: boolean;       // ¿Está completamente configurado?
  configProgress: MarketConfigProgress;

  // Configuración financiera
  primaryCurrency: string;     // ID de moneda principal
  currencies: MarketCurrency[]; // Monedas aceptadas
  minimumWage?: MinimumWage;   // Salario mínimo

  // Catálogos específicos del mercado
  subsidyPlans: SubsidyPlan[];
  cities: MarketCity[];

  // Regulaciones
  regulations: MarketRegulations;

  // Metadata
  notes?: string;              // Notas adicionales
  createdAt: Date;
  updatedAt: Date;
}

/**
 * DTO para crear un nuevo mercado
 */
export interface CreateMarketDTO {
  countryId: string;
  isoCode: string;
  name: string;
  flag?: string;
  primaryCurrency: string;
}

/**
 * DTO para actualizar configuración de un mercado
 */
export interface UpdateMarketDTO {
  status?: MarketStatus;
  isActive?: boolean;
  primaryCurrency?: string;
  minimumWage?: MinimumWage;
  regulations?: Partial<MarketRegulations>;
  notes?: string;
}

/**
 * Estadísticas de un mercado
 */
export interface MarketStats {
  marketId: string;
  totalProjects: number;       // Proyectos en este mercado
  activeProjects: number;      // Proyectos activos
  totalCompanies: number;      // Desarrolladoras operando
  totalRevenue?: number;       // Revenue total (opcional)
  currency: string;            // Moneda de las estadísticas
}

// ========== CATÁLOGO GLOBAL DE PAÍSES ==========

/**
 * País en el catálogo global (para selección)
 */
export interface GlobalCountry {
  id: string;
  isoCode: string;             // 'CO', 'CR', 'DO', etc.
  name: string;                // 'Colombia', 'Costa Rica'
  nameEs: string;              // Nombre en español
  nameEn: string;              // Nombre en inglés
  flag: string;                // Emoji de bandera
  region: string;              // 'Latin America', 'North America', etc.
  isPopular: boolean;          // Para mostrar primero en búsquedas
  defaultCurrency?: string;    // Moneda por defecto del país
}

/**
 * Filtros para buscar países en catálogo global
 */
export interface CountrySearchFilters {
  query?: string;              // Búsqueda por nombre o código
  region?: string;             // Filtrar por región
  popularOnly?: boolean;       // Solo países populares
  excludeIds?: string[];       // Excluir países ya agregados
}
